<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="rules"
    class="register-form"
    @keyup.enter="handleSubmit"
  >
    <el-form-item prop="realName">
      <el-input
        v-model="form.realName"
        placeholder="请输入真实姓名"
        size="large"
        :prefix-icon="User"
        maxlength="20"
      />
    </el-form-item>

    <el-form-item prop="phone">
      <el-input
        v-model="form.phone"
        placeholder="请输入手机号"
        size="large"
        :prefix-icon="Phone"
        maxlength="11"
      />
    </el-form-item>

    <el-form-item prop="captchaCode">
      <div class="captcha-row">
        <el-input
          v-model="form.captchaCode"
          placeholder="请输入图形验证码"
          size="large"
          :prefix-icon="CircleCheck"
          maxlength="6"
          class="captcha-input"
        />
        <div
          class="captcha-image"
          :class="{ loading: captchaLoading }"
          @click="refreshCaptcha"
        >
          <img v-if="captchaImage" :src="captchaImage" alt="验证码" />
          <el-icon v-else><Loading /></el-icon>
        </div>
      </div>
    </el-form-item>

    <el-form-item prop="smsCode">
      <div class="sms-code-row">
        <el-input
          v-model="form.smsCode"
          placeholder="请输入短信验证码"
          size="large"
          :prefix-icon="Message"
          maxlength="6"
          class="sms-input"
        />
        <el-button
          size="large"
          :disabled="countdown > 0 || smsLoading"
          :loading="smsLoading"
          class="send-btn"
          @click="sendSmsCode"
        >
          {{ countdown > 0 ? `${countdown}s后重试` : '获取验证码' }}
        </el-button>
      </div>
    </el-form-item>

    <el-form-item prop="password">
      <el-input
        v-model="form.password"
        type="password"
        placeholder="请设置密码（6-20位）"
        size="large"
        :prefix-icon="Lock"
        show-password
      />
    </el-form-item>

    <el-form-item prop="confirmPassword">
      <el-input
        v-model="form.confirmPassword"
        type="password"
        placeholder="请确认密码"
        size="large"
        :prefix-icon="Lock"
        show-password
      />
    </el-form-item>

    <el-form-item prop="agreement">
      <el-checkbox v-model="form.agreement">
        <span class="agreement-text">
          我已阅读并同意
          <el-button link type="primary" @click.stop="showTerms">用户协议</el-button>
          和
          <el-button link type="primary" @click.stop="showPrivacy">隐私政策</el-button>
        </span>
      </el-checkbox>
    </el-form-item>

    <el-button
      type="primary"
      size="large"
      class="submit-btn"
      :loading="loading"
      @click="handleSubmit"
    >
      注册
    </el-button>
  </el-form>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { Phone, Message, Lock, User, CircleCheck, Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import { sendRegisterSmsCode, register } from '@/api/auth'
import { useUserStore } from '@/stores/user'
import type { ApiResponse } from '@/types'
import type { LoginResponse } from '@/types/user'
import { useCaptcha } from '@/composables/useCaptcha'

const emit = defineEmits<{
  success: []
  'switch-to-login': [phone: string]
}>()

const { captchaLoading, captchaImage, captchaKey, refreshCaptcha } = useCaptcha()

onMounted(() => {
  refreshCaptcha()
})

const userStore = useUserStore()
const formRef = ref<FormInstance>()
const loading = ref(false)
const smsLoading = ref(false)
const countdown = ref(0)
let countdownTimer: number | null = null

const form = ref({
  phone: '',
  captchaCode: '',
  smsCode: '',
  password: '',
  confirmPassword: '',
  realName: '',
  agreement: false,
})

const validateConfirmPassword = (_rule: any, value: string, callback: any) => {
  if (value !== form.value.password) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const rules: FormRules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' },
  ],
  smsCode: [
    { required: true, message: '请输入短信验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' },
  ],
  password: [
    { required: true, message: '请设置密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度6-20位', trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, message: '请确认密码', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' },
  ],
  realName: [
    { required: true, message: '请输入真实姓名', trigger: 'blur' },
    { min: 2, max: 20, message: '姓名长度2-20位', trigger: 'blur' },
  ],
  agreement: [
    {
      validator: (_rule: any, value: boolean, callback: any) => {
        if (!value) {
          callback(new Error('请同意用户协议和隐私政策'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
}

const startCountdown = () => {
  countdown.value = 60
  countdownTimer = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0 && countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }, 1000)
}

const sendSmsCode = async () => {
  const phoneValid = await formRef.value?.validateField('phone').catch(() => false)
  const captchaValid = await formRef.value?.validateField('captchaCode').catch(() => false)
  if (!phoneValid || !captchaValid) return

  smsLoading.value = true
  try {
    await sendRegisterSmsCode(form.value.phone, captchaKey.value, form.value.captchaCode)
    ElMessage.success('验证码已发送')
    startCountdown()
  } catch (error: any) {
    if (error.message?.includes('已注册')) {
      emit('switch-to-login', form.value.phone)
    }
    refreshCaptcha()
    form.value.captchaCode = ''
  } finally {
    smsLoading.value = false
  }
}

const handleSubmit = async () => {
  if (!formRef.value) return

  await formRef.value.validate(async (valid) => {
    if (!valid) return

    loading.value = true
    try {
      const res: ApiResponse<LoginResponse> = await register({
        phone: form.value.phone,
        password: form.value.password,
        smsCode: form.value.smsCode,
        realName: form.value.realName,
      })

      // 转换后端返回数据为前端用户状态
      const userState = {
        userId: res.data.userId,
        realName: res.data.realName,
        phone: res.data.phone,
        avatar: res.data.avatar,
        isVip: res.data.isVip,
        vipExpireTime: res.data.vipExpireTime,
      }

      userStore.setUserInfo(userState)
      userStore.setToken(res.data.accessToken)
      userStore.setRefreshToken(res.data.refreshToken)

      ElMessage.success('注册成功')
      emit('success')
    } catch (error) {
      // 错误已在拦截器处理
    } finally {
      loading.value = false
    }
  })
}

const showTerms = () => {
  // TODO: 显示用户协议弹窗
  ElMessage.info('用户协议功能待实现')
}

const showPrivacy = () => {
  // TODO: 显示隐私政策弹窗
  ElMessage.info('隐私政策功能待实现')
}

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped lang="scss">
.register-form {
  :deep(.el-input__inner) {
    height: 44px;
  }
}

.sms-code-row {
  display: flex;
  gap: 12px;

  .sms-input {
    flex: 1;
  }

  .send-btn {
    width: 120px;
    height: 44px;
    padding: 0;
  }
}

.captcha-row {
  display: flex;
  gap: 12px;

  .captcha-input {
    flex: 1;
  }

  .captcha-image {
    width: 120px;
    height: 44px;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &.loading {
      opacity: 0.6;
    }
  }
}

.agreement-text {
  font-size: 13px;
  color: var(--text-secondary);

  .el-button {
    font-size: 13px;
    padding: 0;
    height: auto;
    vertical-align: baseline;
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
}
</style>
